import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import employeeService from '../../services/employeeService';

const UserTrackSettingsContext = createContext({
  loading: true,
  canSeeScreenshots: false,
  canDeleteScreenshots: false,
  canSeeWorkSummary: false,
  canSeeTimesheet: false,
  canSeeTimelapse: false,
  canSeeManualTime: false,
  projectManagementEnabled: false,
  payrollEnabled: true,
  error: '',
  refresh: () => {},
});

export function UserTrackSettingsProvider({ children }) {
  const [loading, setLoading] = useState(true);
  const [settings, setSettings] = useState({
    canSeeScreenshots: false,
    canDeleteScreenshots: false,
    canSeeWorkSummary: false,
    canSeeTimesheet: false,
    canSeeTimelapse: false,
    canSeeManualTime: false,
    projectManagementEnabled: false,
    payrollEnabled: true,
  });
  const [error, setError] = useState('');

  const fetchSettings = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const raw = localStorage.getItem('user');
      if (!raw) {
        setSettings({ canSeeScreenshots: false, canDeleteScreenshots: false, canSeeWorkSummary: false, canSeeTimesheet: false, canSeeTimelapse: false, canSeeManualTime: false, projectManagementEnabled: false });
        return;
      }
      const user = JSON.parse(raw);
      const userId = user?.id;
      if (!userId) {
        setSettings({ canSeeScreenshots: false, canDeleteScreenshots: false, canSeeWorkSummary: false, canSeeTimesheet: false, canSeeTimelapse: false, canSeeManualTime: false, projectManagementEnabled: false });
        return;
      }
      const data = await employeeService.getEmployeeTrackSettings(userId);
      const resolve = (v) => v === true || v === 'true' || v === 1;
      const workSummaryVal = data?.allow_employee_see_work_summary !== undefined ? data?.allow_employee_see_work_summary : data?.allow_employee_see_timesheet;
      setSettings({
        canSeeScreenshots: resolve(data?.allow_employee_see_screenshot),
        canDeleteScreenshots: resolve(data?.allow_employee_delete_screenshot),
        canSeeWorkSummary: resolve(workSummaryVal),
        canSeeTimesheet: resolve(data?.allow_employee_see_timesheet),
        canSeeTimelapse: resolve(data?.allow_employee_see_timelapse),
        canSeeManualTime: resolve(data?.manual_time_enabled),
        projectManagementEnabled: resolve(data?.project_management_enabled),
        payrollEnabled: data?.payroll_enabled !== false,
      });
    } catch (err) {
      console.error('[UserTrackSettings] fetch failed:', err);
      setSettings({ canSeeScreenshots: false, canDeleteScreenshots: false, canSeeWorkSummary: false, canSeeTimesheet: false, canSeeTimelapse: false, canSeeManualTime: false, projectManagementEnabled: false, payrollEnabled: false });
      setError('Your workspace permissions could not be refreshed. Some navigation items may be temporarily hidden.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  useEffect(() => {
    const handler = () => fetchSettings();
    window.addEventListener('userUpdated', handler);
    window.addEventListener('trackingSettingsUpdated', handler);
    return () => {
      window.removeEventListener('userUpdated', handler);
      window.removeEventListener('trackingSettingsUpdated', handler);
    };
  }, [fetchSettings]);

  const value = {
    loading,
    canSeeScreenshots: settings.canSeeScreenshots,
    canDeleteScreenshots: settings.canDeleteScreenshots,
    canSeeWorkSummary: settings.canSeeWorkSummary,
    canSeeTimesheet: settings.canSeeTimesheet,
    canSeeTimelapse: settings.canSeeTimelapse,
    canSeeManualTime: settings.canSeeManualTime,
    projectManagementEnabled: settings.projectManagementEnabled,
    payrollEnabled: settings.payrollEnabled,
    error,
    refresh: fetchSettings,
  };

  return (
    <UserTrackSettingsContext.Provider value={value}>
      {children}
    </UserTrackSettingsContext.Provider>
  );
}

export function useUserTrackSettings() {
  return useContext(UserTrackSettingsContext);
}
